import { useEffect } from "react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useOrgBranding } from "@/hooks/useOrgBranding";
import { structuredLogger } from "@/lib/logging";
import { applyLocalAppFont } from "@/lib/fontPolicy";

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { settings, loading } = useAppSettings();
  const { isDark } = useDarkMode();
  const { branding: orgBranding } = useOrgBranding();

  useEffect(() => {
    // Skip if still loading or no settings
    if (loading || !settings) return;
    
    try {
      const root = document.documentElement;

      // Apply dark mode
      root.classList.toggle("dark", isDark);

      // Determine which colors to use: org-specific or app-level
      const primaryColor = orgBranding.primary_color || settings.primary_color;
      const secondaryColor = orgBranding.secondary_color || settings.secondary_color;
      const accentColor = orgBranding.accent_color || settings.accent_color;

      // Apply primary color (GG Blue default: #1E3A8A)
      // Skip in dark mode to preserve high-contrast token defined in index.css
      if (primaryColor && !isDark) {
        try {
          const hsl = hexToHSL(primaryColor);
          root.style.setProperty("--primary", hsl);
          root.style.setProperty("--ring", hsl);
          root.style.setProperty("--gg-blue", hsl);
        } catch (e) {
          structuredLogger.warn("Error applying primary color", { error: e instanceof Error ? e.message : "Unknown" });
        }
      } else if (isDark) {
        // Clear any previously set inline overrides so dark tokens apply
        root.style.removeProperty("--primary");
        root.style.removeProperty("--ring");
        root.style.removeProperty("--gg-blue");
      }

      // Apply secondary color (GG Indigo default: #4F46E5)
      if (secondaryColor) {
        try {
          const hsl = hexToHSL(secondaryColor);
          root.style.setProperty("--secondary", hsl);
          root.style.setProperty("--gg-indigo", hsl);
        } catch (e) {
          structuredLogger.warn("Error applying secondary color", { error: e instanceof Error ? e.message : "Unknown" });
        }
      }

      // Apply accent color (Electric Cyan default: #00E5FF)
      if (accentColor) {
        try {
          const hsl = hexToHSL(accentColor);
          root.style.setProperty("--accent", hsl);
          root.style.setProperty("--gg-cyan", hsl);
        } catch (e) {
          structuredLogger.warn("Error applying accent color", { error: e instanceof Error ? e.message : "Unknown" });
        }
      }

      // Bundled fonts only; clear any prior custom remote stylesheet.
      applyLocalAppFont(root, document, settings.font_family);
    } catch (error) {
      structuredLogger.error("Error in ThemeProvider", { error: error instanceof Error ? error.message : "Unknown" });
    }
  }, [settings, isDark, loading, orgBranding]);

  // Always render children - don't block on loading
  return <>{children}</>;
};

// Helper function to convert hex to HSL
function hexToHSL(hex: string): string {
  try {
    hex = hex.replace(/^#/, "");
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  } catch (error) {
    structuredLogger.warn("Error converting hex to HSL", { error: error instanceof Error ? error.message : "Unknown" });
    return "224 64% 33%"; // Default to GG Blue
  }
}
